import * as React from "react"

import { cn } from "@/lib/utils"
import {
  Pagination,
  PaginationControls,
  PaginationNext,
  PaginationPrevious,
  PaginationStatus,
} from "./pagination"

const TABLE_PAGE_SIZE = 15

function Table({ className, children, ...props }: React.ComponentProps<"table">) {
  const labels = getTableLabels(children)
  const responsiveChildren = addMobileLabels(children, labels)
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto rounded-t-[18px] bg-white p-3 max-sm:overflow-visible dark:bg-card"
    >
      <table
        data-slot="table"
        className={cn("w-full border-separate border-spacing-0 caption-bottom text-sm max-sm:block max-sm:min-w-0", className)}
        {...props}
      >
        {responsiveChildren}
      </table>
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("bg-transparent max-sm:hidden", className)}
      {...props}
    />
  )
}

function TableBody({ className, children, ...props }: React.ComponentProps<"tbody">) {
  const rows = React.Children.toArray(children)
  const pageCount = Math.max(1, Math.ceil(rows.length / TABLE_PAGE_SIZE))
  const [page, setPage] = React.useState(0)
  const safePage = Math.min(page, pageCount - 1)
  const visibleRows = rows.slice(safePage * TABLE_PAGE_SIZE, (safePage + 1) * TABLE_PAGE_SIZE)

  React.useEffect(() => {
    if (page >= pageCount) setPage(pageCount - 1)
  }, [page, pageCount])

  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child_td]:border-b-0 max-sm:grid max-sm:gap-3", className)}
      {...props}
    >
      {visibleRows}
      {pageCount > 1 ? (
        <tr data-slot="table-pagination-row">
          <td className="border-0 px-1 pb-1 pt-4" colSpan={100}>
            <Pagination>
              <PaginationStatus>
                Page {safePage + 1} of {pageCount} · {rows.length} rows
              </PaginationStatus>
              <PaginationControls>
                <PaginationPrevious disabled={safePage === 0} onClick={() => setPage((current) => Math.max(0, current - 1))} />
                <PaginationNext disabled={safePage === pageCount - 1} onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))} />
              </PaginationControls>
            </Pagination>
          </td>
        </tr>
      ) : null}
    </tbody>
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "transition-colors [&>td]:transition-colors hover:[&>td]:bg-muted/50 hover:[&>td:first-child]:rounded-l-lg hover:[&>td:last-child]:rounded-r-lg aria-current:[&>td]:bg-accent/45 aria-current:[&>td:first-child]:rounded-l-lg aria-current:[&>td:last-child]:rounded-r-lg has-aria-expanded:[&>td]:bg-muted/50 data-[state=selected]:[&>td]:bg-muted data-[state=selected]:[&>td:first-child]:rounded-l-lg data-[state=selected]:[&>td:last-child]:rounded-r-lg max-sm:block max-sm:rounded-2xl max-sm:border max-sm:bg-white max-sm:p-3 max-sm:hover:bg-muted/50 max-sm:hover:[&>td]:bg-transparent dark:max-sm:bg-card",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-10 border-b-2 border-card bg-[var(--table-header)] px-2 text-left align-middle text-xs font-medium uppercase tracking-wide whitespace-nowrap text-muted-foreground first:rounded-l-lg last:rounded-r-lg [&:has([role=checkbox])]:pr-0",
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, colSpan, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "border-b border-border/70 p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 max-sm:grid max-sm:grid-cols-[minmax(0,7rem)_minmax(0,1fr)] max-sm:items-center max-sm:gap-3 max-sm:px-1 max-sm:py-2 max-sm:text-left max-sm:whitespace-normal max-sm:before:text-xs max-sm:before:font-medium max-sm:before:uppercase max-sm:before:tracking-wide max-sm:before:text-muted-foreground max-sm:before:content-[attr(data-mobile-label)] max-sm:last:border-b-0 max-sm:[&>*]:min-w-0 max-sm:data-[mobile-action=true]:[&>*]:w-fit max-sm:data-[mobile-action=true]:[&>*]:justify-self-start",
        colSpan && colSpan > 1 && "max-sm:block max-sm:w-full",
        className
      )}
      colSpan={colSpan}
      {...props}
    />
  )
}

function getTableLabels(children: React.ReactNode) {
  const headers: string[] = []
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child) || child.type !== TableHeader) return
    const headerProps = child.props as { children?: React.ReactNode }
    React.Children.forEach(headerProps.children, (row) => {
      if (!React.isValidElement(row) || row.type !== TableRow) return
      const rowProps = row.props as { children?: React.ReactNode }
      React.Children.forEach(rowProps.children, (cell) => {
        if (!React.isValidElement(cell) || cell.type !== TableHead) return
        const cellProps = cell.props as { children?: React.ReactNode }
        headers.push(getNodeText(cellProps.children))
      })
    })
  })
  return headers
}

function getNodeText(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (!React.isValidElement(node)) return ''
  const props = node.props as { children?: React.ReactNode }
  return React.Children.toArray(props.children).map(getNodeText).join(' ').trim()
}

function addMobileLabels(children: React.ReactNode, labels: string[]): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child) || child.type !== TableBody) return child
    const bodyProps = child.props as { children?: React.ReactNode }
    return React.cloneElement(child, undefined, React.Children.map(bodyProps.children, (row) => {
      if (!React.isValidElement(row) || row.type !== TableRow) return row
      const rowProps = row.props as { children?: React.ReactNode }
      let cellIndex = 0
      return React.cloneElement(row, undefined, React.Children.map(rowProps.children, (cell) => {
        if (!React.isValidElement(cell) || cell.type !== TableCell) return cell
        const cellProps = cell.props as React.ComponentProps<'td'>
        const label = cellProps.colSpan && cellProps.colSpan > 1 ? '' : labels[cellIndex] ?? ''
        cellIndex += cellProps.colSpan ?? 1
        return React.cloneElement(cell as React.ReactElement<React.ComponentProps<'td'> & { 'data-mobile-action'?: boolean; 'data-mobile-label'?: string }>, {
          'data-mobile-action': label.trim().toLowerCase() === 'action',
          'data-mobile-label': label,
        })
      }))
    }))
  })
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
